import React, { useState, useEffect } from 'react';
import CustomerDetailsModal from '../components/CustomerDetailsModel';
import customerService from '../../../services/customerService';

const PAGE_SIZE = 10;

const formatCurrency = (amount) => {
  const num = Math.round(parseFloat(amount || 0));
  return 'Rs ' + num.toLocaleString('en-US');
};

// ── Pagination Control ────────────────────────────────────────────────────────
const Pagination = ({ currentPage, totalPages, onPageChange, totalItems, pageSize }) => {
  if (totalPages <= 1) return null;
  const start = (currentPage - 1) * pageSize + 1;
  const end   = Math.min(currentPage * pageSize, totalItems);
  return (
    <div style={paginationStyles.wrapper}>
      <span style={paginationStyles.info}>Showing {start}–{end} of {totalItems} customers</span>
      <div style={paginationStyles.controls}>
        <button onClick={() => onPageChange(1)}               disabled={currentPage === 1}          style={{ ...paginationStyles.btn, ...(currentPage === 1          ? paginationStyles.btnDisabled : {}) }}>«</button>
        <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}          style={{ ...paginationStyles.btn, ...(currentPage === 1          ? paginationStyles.btnDisabled : {}) }}>‹ Prev</button>
        <span style={paginationStyles.pageIndicator}>Page {currentPage} of {totalPages}</span>
        <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} style={{ ...paginationStyles.btn, ...(currentPage === totalPages ? paginationStyles.btnDisabled : {}) }}>Next ›</button>
        <button onClick={() => onPageChange(totalPages)}      disabled={currentPage === totalPages} style={{ ...paginationStyles.btn, ...(currentPage === totalPages ? paginationStyles.btnDisabled : {}) }}>»</button>
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const CustomersTab = () => {
  const [customers,        setCustomers]        = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [error,            setError]            = useState(null);
  const [showAddModal,     setShowAddModal]     = useState(false);
  const [showEditModal,    setShowEditModal]    = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Search & filter
  const [searchQuery,  setSearchQuery]  = useState('');
  const [filterTerms,  setFilterTerms]  = useState('All'); // All | Cash | Credit
  const [currentPage,  setCurrentPage]  = useState(1);

  const [formData, setFormData] = useState({
    name: '', phone: '', address: '', payment_terms: 'Cash', credit_limit: '', notes: '', preferred_session: '',
  });

  useEffect(() => { fetchCustomers(); }, []);

  // Reset to page 1 whenever search or filter changes
  useEffect(() => { setCurrentPage(1); }, [searchQuery, filterTerms]);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await customerService.getAllCustomers();
      setCustomers(response.data || []);
      setCurrentPage(1);
      setLoading(false);
    } catch (err) {
      console.error('❌ Fetch customers error:', err);
      setError('Failed to load customers');
      setLoading(false);
    }
  };

  const handleAddCustomer = async (e) => {
    e.preventDefault();
    try {
      await customerService.createCustomer(formData);
      alert('✅ Customer added successfully!');
      setShowAddModal(false); resetForm(); fetchCustomers();
    } catch (err) {
      alert('❌ ' + (err.response?.data?.message || 'Failed to add customer'));
    }
  };

  const handleEditCustomer = async (e) => {
    e.preventDefault();
    try {
      await customerService.updateCustomer(selectedCustomer.id, formData);
      alert('✅ Customer updated successfully!');
      setShowEditModal(false); setSelectedCustomer(null); resetForm(); fetchCustomers();
    } catch (err) {
      alert('❌ ' + (err.response?.data?.message || 'Failed to update customer'));
    }
  };

  const handleDeleteCustomer = async (customer) => {
    if (!window.confirm(`Are you sure you want to delete ${customer.name}?`)) return;
    try {
      await customerService.deleteCustomer(customer.id);
      alert('✅ Customer deleted successfully!');
      setShowEditModal(false); setSelectedCustomer(null); fetchCustomers();
    } catch (err) {
      alert('❌ ' + (err.response?.data?.message || 'Failed to delete customer'));
    }
  };

  const openEditModal = (customer) => {
    setSelectedCustomer(customer);
    setFormData({ name: customer.name, phone: customer.phone, address: customer.address, payment_terms: customer.payment_terms, credit_limit: customer.credit_limit || '', notes: customer.notes || '', preferred_session: customer.preferred_session || '' });
    setShowEditModal(true);
  };

  const openDetailsModal = (customer) => { setSelectedCustomer(customer); setShowDetailsModal(true); };
  const resetForm = () => setFormData({ name: '', phone: '', address: '', payment_terms: 'Cash', credit_limit: '', notes: '', preferred_session: '' });

  // ── Derived data ──────────────────────────────────────────────────────────
  const totalOutstanding = customers.reduce((sum, c) => sum + parseFloat(c.outstanding_balance || 0), 0);

  const filteredCustomers = customers.filter(c => {
    const matchesSearch = !searchQuery ||
      c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (c.phone || '').includes(searchQuery) ||
      (c.address || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterTerms === 'All' || c.payment_terms === filterTerms;
    return matchesSearch && matchesFilter;
  });

  const totalPages     = Math.ceil(filteredCustomers.length / PAGE_SIZE);
  const paginatedCustomers = filteredCustomers.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  if (loading) return (
    <div style={{ padding: '40px', textAlign: 'center' }}>
      <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
      <h2 style={{ color: '#666' }}>Loading customers...</h2>
    </div>
  );
  if (error) return (
    <div style={{ padding: '40px', textAlign: 'center' }}>
      <div style={{ fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
      <h2 style={{ color: '#e74c3c', marginBottom: '10px' }}>Error</h2>
      <p style={{ color: '#666' }}>{error}</p>
      <button onClick={fetchCustomers} style={styles.retryButton}>🔄 Retry</button>
    </div>
  );

  return (
    <div>
      {/* ── Header ── */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '20px' }}>
        <div>
          <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#2c3e50', marginBottom: '8px' }}>Customers Management</h2>
          <p style={{ color: '#7f8c8d', fontSize: '14px' }}>Manage customer details, payment terms, and credit accounts</p>
        </div>
        <button onClick={() => setShowAddModal(true)} style={styles.addButton}>➕ Add Customer</button>
      </div>

      {/* ── Summary Cards ── */}
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{customers.length}</div>
          <div style={styles.statLabel}>Total Customers</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{customers.filter(c => c.payment_terms === 'Cash').length}</div>
          <div style={styles.statLabel}>Cash Customers</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{customers.filter(c => c.payment_terms === 'Credit').length}</div>
          <div style={styles.statLabel}>Credit Customers</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statValue, color: '#e74c3c' }}>{formatCurrency(totalOutstanding)}</div>
          <div style={styles.statLabel}>Total Outstanding</div>
        </div>
      </div>

      {/* ── Search & Filter Bar ── */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Search input */}
        <div style={{ flex: 1, minWidth: '200px', position: 'relative' }}>
          <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '16px', color: '#95a5a6' }}>🔍</span>
          <input
            type="text"
            placeholder="Search by name, phone, or address..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ ...styles.searchInput, paddingLeft: '38px' }}
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', color: '#95a5a6' }}>✕</button>
          )}
        </div>

        {/* Filter buttons */}
        <div style={{ display: 'flex', gap: '6px' }}>
          {['All', 'Cash', 'Credit'].map(term => (
            <button key={term} onClick={() => setFilterTerms(term)} style={{
              padding: '8px 16px', border: '2px solid',
              borderColor: filterTerms === term ? '#27ae60' : '#e0e0e0',
              borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600',
              background: filterTerms === term ? '#27ae60' : 'white',
              color:      filterTerms === term ? 'white'   : '#7f8c8d',
            }}>
              {term === 'All' ? `All (${customers.length})` : term === 'Cash' ? `💵 Cash (${customers.filter(c => c.payment_terms === 'Cash').length})` : `💳 Credit (${customers.filter(c => c.payment_terms === 'Credit').length})`}
            </button>
          ))}
        </div>

        {/* Result count */}
        {(searchQuery || filterTerms !== 'All') && (
          <span style={{ fontSize: '13px', color: '#7f8c8d', whiteSpace: 'nowrap' }}>
            {filteredCustomers.length} result{filteredCustomers.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* ── Customers Table ── */}
      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.headerRow}>
              <th style={styles.th}>#</th>
              <th style={styles.th}>Customer Name</th>
              <th style={styles.th}>Phone</th>
              <th style={styles.th}>Address</th>
              <th style={styles.th}>Session</th>
              <th style={styles.th}>Payment Terms</th>
              <th style={styles.th}>Credit Limit</th>
              <th style={styles.th}>Outstanding Balance</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCustomers.length === 0 ? (
              <tr>
                <td colSpan="9" style={styles.noData}>
                  <div style={{ fontSize: '48px', marginBottom: '10px' }}>👥</div>
                  {searchQuery || filterTerms !== 'All' ? 'No customers match your search' : 'No customers found'}
                </td>
              </tr>
            ) : (
              paginatedCustomers.map((customer, idx) => (
                <tr key={customer.id} style={styles.row}>
                  <td style={{ ...styles.td, color: '#95a5a6', fontSize: '13px', width: '40px' }}>
                    {(currentPage - 1) * PAGE_SIZE + idx + 1}
                  </td>
                  <td style={styles.td}><strong>{customer.name}</strong></td>
                  <td style={styles.td}>{customer.phone}</td>
                  <td style={styles.td}>{customer.address || '—'}</td>
                  <td style={styles.td}>
                    {customer.preferred_session === 'Morning' ? <span style={{ background: '#fff3cd', color: '#856404', padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>🌅 Morning</span>
                     : customer.preferred_session === 'Evening' ? <span style={{ background: '#d1ecf1', color: '#0c5460', padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>🌆 Evening</span>
                     : <span style={{ color: '#95a5a6' }}>—</span>}
                  </td>
                  <td style={styles.td}>
                    <span style={getPaymentTermsStyle(customer.payment_terms)}>{customer.payment_terms}</span>
                  </td>
                  <td style={styles.td}>
                    {customer.payment_terms === 'Credit' && customer.credit_limit ? formatCurrency(customer.credit_limit) : '—'}
                  </td>
                  <td style={styles.td}>
                    {parseFloat(customer.outstanding_balance || 0) > 0
                      ? <strong style={{ color: '#e74c3c' }}>{formatCurrency(customer.outstanding_balance)}</strong>
                      : <span style={{ color: '#27ae60' }}>Rs 0</span>}
                  </td>
                  <td style={styles.td}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button onClick={() => openEditModal(customer)}    style={styles.editButton}>✏️ Edit</button>
                      <button onClick={() => openDetailsModal(customer)} style={styles.viewButton}>👁️ View</button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          totalItems={filteredCustomers.length}
          pageSize={PAGE_SIZE}
        />
      </div>

      {/* ── Add Customer Modal ── */}
      {showAddModal && (
        <div style={styles.modalOverlay} onClick={() => setShowAddModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>Add New Customer</h2>
            <form onSubmit={handleAddCustomer}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Customer Name <span style={{ color: '#e74c3c' }}>*</span></label>
                <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} style={styles.input} placeholder="Enter customer name" required />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Phone Number <span style={{ color: '#e74c3c' }}>*</span></label>
                <input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} style={styles.input} placeholder="03XX-XXXXXXX" required />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Address</label>
                <textarea value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} style={{ ...styles.input, minHeight: '60px', resize: 'vertical' }} rows="2" placeholder="Customer address" />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Payment Terms <span style={{ color: '#e74c3c' }}>*</span></label>
                <select value={formData.payment_terms} onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })} style={styles.input} required>
                  <option value="Cash">Cash (Immediate Payment)</option>
                  <option value="Credit">Credit (Payment on Terms)</option>
                </select>
              </div>
              {formData.payment_terms === 'Credit' && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Credit Limit (PKR)</label>
                  <input type="number" value={formData.credit_limit} onChange={(e) => setFormData({ ...formData, credit_limit: e.target.value })} style={styles.input} placeholder="Maximum credit amount" />
                </div>
              )}
              <div style={styles.formGroup}>
                <label style={styles.label}>Preferred Session <span style={{ color: '#7f8c8d', fontSize: '11px' }}>(optional)</span></label>
                <select value={formData.preferred_session} onChange={(e) => setFormData({ ...formData, preferred_session: e.target.value })} style={styles.input}>
                  <option value="">Not assigned</option>
                  <option value="Morning">🌅 Morning</option>
                  <option value="Evening">🌆 Evening</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Notes</label>
                <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} style={{ ...styles.input, minHeight: '60px', resize: 'vertical' }} rows="2" placeholder="Additional notes" />
              </div>
              <div style={styles.modalButtons}>
                <button type="button" onClick={() => { setShowAddModal(false); resetForm(); }} style={styles.cancelButton}>Cancel</button>
                <button type="submit" style={styles.submitButton}>Add Customer</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Edit Customer Modal ── */}
      {showEditModal && (
        <div style={styles.modalOverlay} onClick={() => setShowEditModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>Edit Customer</h2>
            <form onSubmit={handleEditCustomer}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Customer Name <span style={{ color: '#e74c3c' }}>*</span></label>
                <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} style={styles.input} required />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Phone Number <span style={{ color: '#e74c3c' }}>*</span></label>
                <input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} style={styles.input} required />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Address</label>
                <textarea value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} style={{ ...styles.input, minHeight: '60px', resize: 'vertical' }} rows="2" />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Payment Terms <span style={{ color: '#e74c3c' }}>*</span></label>
                <select value={formData.payment_terms} onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })} style={styles.input} required>
                  <option value="Cash">Cash (Immediate Payment)</option>
                  <option value="Credit">Credit (Payment on Terms)</option>
                </select>
              </div>
              {formData.payment_terms === 'Credit' && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Credit Limit (PKR)</label>
                  <input type="number" value={formData.credit_limit} onChange={(e) => setFormData({ ...formData, credit_limit: e.target.value })} style={styles.input} />
                </div>
              )}
              <div style={styles.formGroup}>
                <label style={styles.label}>Preferred Session <span style={{ color: '#7f8c8d', fontSize: '11px' }}>(optional)</span></label>
                <select value={formData.preferred_session} onChange={(e) => setFormData({ ...formData, preferred_session: e.target.value })} style={styles.input}>
                  <option value="">Not assigned</option>
                  <option value="Morning">🌅 Morning</option>
                  <option value="Evening">🌆 Evening</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Notes</label>
                <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} style={{ ...styles.input, minHeight: '60px', resize: 'vertical' }} rows="2" />
              </div>
              <div style={styles.modalButtons}>
                <button type="submit" style={styles.submitButton}>Update Customer</button>
                <button type="button" onClick={() => handleDeleteCustomer(selectedCustomer)} style={styles.deleteButton}>🗑️ Delete</button>
                <button type="button" onClick={() => { setShowEditModal(false); setSelectedCustomer(null); resetForm(); }} style={styles.cancelButton}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Customer Details Modal ── */}
      {showDetailsModal && selectedCustomer && (
        <CustomerDetailsModal
          customer={selectedCustomer}
          onClose={() => { setShowDetailsModal(false); setSelectedCustomer(null); }}
          onPaymentAdded={() => { fetchCustomers(); }}
        />
      )}
    </div>
  );
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const getPaymentTermsStyle = (terms) =>
  terms === 'Cash'
    ? { background: '#d4edda', color: '#155724', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }
    : { background: '#fff3cd', color: '#856404', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' };

// ── Pagination Styles ─────────────────────────────────────────────────────────
const paginationStyles = {
  wrapper:       { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderTop: '1px solid #f0f0f0', background: '#fafafa', borderRadius: '0 0 12px 12px', flexWrap: 'wrap', gap: '10px' },
  info:          { fontSize: '13px', color: '#7f8c8d' },
  controls:      { display: 'flex', alignItems: 'center', gap: '6px' },
  btn:           { padding: '6px 12px', border: '1px solid #ddd', borderRadius: '6px', background: 'white', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#2c3e50' },
  btnDisabled:   { opacity: 0.4, cursor: 'not-allowed' },
  pageIndicator: { padding: '6px 14px', background: '#27ae60', color: 'white', borderRadius: '6px', fontSize: '13px', fontWeight: '600' },
};

// ── Component Styles ──────────────────────────────────────────────────────────
const styles = {
  addButton:      { padding: '10px 20px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  statsGrid:      { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '24px' },
  statCard:       { background: 'white', padding: '24px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', textAlign: 'center' },
  statValue:      { fontSize: '32px', fontWeight: '700', color: '#27ae60', marginBottom: '8px' },
  statLabel:      { fontSize: '13px', color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: '0.5px' },
  searchInput:    { width: '100%', padding: '10px 12px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', outline: 'none' },
  tableContainer: { background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'auto' },
  table:          { width: '100%', borderCollapse: 'collapse' },
  headerRow:      { background: '#f8f9fa' },
  th:             { padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '700', color: '#2c3e50', borderBottom: '2px solid #e0e0e0', whiteSpace: 'nowrap' },
  row:            { borderBottom: '1px solid #f0f0f0' },
  td:             { padding: '16px', fontSize: '14px', color: '#2c3e50' },
  noData:         { padding: '60px 20px', textAlign: 'center', color: '#95a5a6', fontSize: '16px' },
  editButton:     { padding: '6px 12px', background: '#3498db', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' },
  viewButton:     { padding: '6px 12px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' },
  retryButton:    { marginTop: '20px', padding: '12px 24px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  modalOverlay:   { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, overflow: 'auto', padding: '20px' },
  modal:          { background: 'white', padding: '30px', borderRadius: '12px', width: '90%', maxWidth: '600px', boxShadow: '0 10px 40px rgba(0,0,0,0.2)', maxHeight: '90vh', overflow: 'auto' },
  modalTitle:     { fontSize: '22px', fontWeight: '700', marginBottom: '24px', color: '#2c3e50' },
  formGroup:      { display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px' },
  label:          { fontSize: '13px', fontWeight: '600', color: '#2c3e50' },
  input:          { width: '100%', padding: '10px 12px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' },
  modalButtons:   { display: 'flex', gap: '12px', marginTop: '24px' },
  cancelButton:   { flex: 1, padding: '12px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  submitButton:   { flex: 1, padding: '12px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  deleteButton:   { flex: 1, padding: '12px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
};

export default CustomersTab;